import { Coordinate } from "PosterEditor/hooks/use-coordinates";
import { GPSData } from "PosterEditor/model/poster-project";

// =============================================================================
// ANALYTICS EVENTS
// =============================================================================

/**
 * Typed analytics event names for the Strava acquisition flow.
 * Using an object const ensures type safety and prevents typos.
 */
export const StravaAnalyticsEvents = {
  // Landing page events
  LANDING_VIEWED: "Strava Landing Viewed",
  OAUTH_INITIATED: "Strava OAuth Initiated",

  // OAuth callback events
  OAUTH_SUCCESS: "Strava OAuth Success",
  OAUTH_ERROR: "Strava OAuth Error",

  // Activities page events
  ACTIVITIES_VIEWED: "Strava Activities Viewed",
  ACTIVITIES_ERROR: "Strava Activities Error",
  ACTIVITY_SELECTED: "Strava Activity Selected",

  // Preview page events
  PREVIEW_LOADED: "Strava Preview Loaded",
  PREVIEW_ERROR: "Strava Preview Error",
  PREVIEW_CTA_CLICKED: "Strava Preview CTA Clicked",
} as const;

/** Type for Strava analytics event names */
export type StravaAnalyticsEvent = typeof StravaAnalyticsEvents[keyof typeof StravaAnalyticsEvents];

/**
 * Error types for Strava OAuth errors.
 */
export const StravaOAuthErrorTypes = {
  OAUTH_DENIED: "oauth_denied",
  MISSING_PARAMS: "missing_params",
  INVALID_STATE: "invalid_state",
  NO_ACTIVITIES: "no_activities",
  EXCHANGE_FAILED: "exchange_failed",
} as const;

export type StravaOAuthErrorType = typeof StravaOAuthErrorTypes[keyof typeof StravaOAuthErrorTypes];

/**
 * Error types for Strava activities page errors.
 */
export const StravaActivitiesErrorTypes = {
  NO_DATA: "no_data",
  EMPTY_LIST: "empty_list",
} as const;

export type StravaActivitiesErrorType = typeof StravaActivitiesErrorTypes[keyof typeof StravaActivitiesErrorTypes];

/**
 * Error types for Strava preview page errors.
 */
export const StravaPreviewErrorTypes = {
  NO_PREVIEW_DATA: "no_preview_data",
  TEMPLATE_NOT_FOUND: "template_not_found",
  LOAD_FAILED: "load_failed",
} as const;

export type StravaPreviewErrorType = typeof StravaPreviewErrorTypes[keyof typeof StravaPreviewErrorTypes];

/**
 * CTA action types for the preview page.
 */
export const StravaPreviewCTAActions = {
  CREATE_ACCOUNT: "create_account",
  CHANGE_TEMPLATE: "change_template",
} as const;

export type StravaPreviewCTAAction = typeof StravaPreviewCTAActions[keyof typeof StravaPreviewCTAActions];

// =============================================================================
// COLLECTION LIST HANDLES
// =============================================================================

/**
 * Valid handles for the collection list page.
 * These correspond to different curated sets of collections.
 *
 * - "A": Default collection list (currently shows Runs and Hikes)
 * - "B": Alternative collection list (for A/B testing or seasonal content)
 * - "featured": Featured/promoted collections
 */
export type CollectionListHandle = "A" | "B" | "featured";

/**
 * Type guard to check if a string is a valid CollectionListHandle.
 */
export const isValidCollectionListHandle = (handle: string): handle is CollectionListHandle => {
  return handle === "A" || handle === "B" || handle === "featured";
};

/**
 * OAuth state stored in localStorage for CSRF protection.
 * State is generated before redirecting to Strava and validated on return.
 */
export interface StravaOAuthState {
  /** Cryptographically secure random state string */
  state: string;
  /** Unix timestamp (ms) when state was created */
  createdAt: number;
  /** URL path to return to after OAuth completes */
  returnTo: string;
}

/**
 * Preview data stored in localStorage for users who haven't created an account yet.
 * This allows users to see their Strava activity on a poster before signing up.
 */
export interface StravaPreviewData {
  /** Strava activity ID */
  activityId: number;
  /** Activity name from Strava */
  activityName: string;
  /** Activity type (e.g., "Run", "Ride", "Hike") */
  activityType: string;
  /** ISO date string of activity start */
  startDate: string;
  /** Decoded GPS coordinates from the activity */
  coordinates: Coordinate[];
  /** Computed GPS data (elevation, distance, times) */
  gpsData: GPSData;
  /** Athlete's first name for personalization */
  athleteFirstName: string;
  /** Unix timestamp (ms) when data was fetched */
  fetchedAt: number;
  /** Distance in meters from Strava API (more accurate than GPS-calculated) */
  stravaDistance: number;
  /** Elevation gain in meters from Strava API (more accurate than GPS-calculated) */
  stravaElevationGain: number;
  /** Moving time in seconds from Strava API */
  stravaMovingTime: number;
}

/**
 * Strava activity metadata from the API.
 * Subset of fields we care about from the activities endpoint.
 */
export interface StravaActivity {
  /** Unique activity ID */
  id: number;
  /** User-provided activity name */
  name: string;
  /** Activity type (Run, Ride, Swim, etc.) */
  type: string;
  /** Sport type (more specific than type) */
  sport_type: string;
  /** ISO 8601 start date (local time) */
  start_date_local: string;
  /** ISO 8601 start date (UTC) */
  start_date: string;
  /** Distance in meters */
  distance: number;
  /** Moving time in seconds */
  moving_time: number;
  /** Elapsed time in seconds */
  elapsed_time: number;
  /** Total elevation gain in meters */
  total_elevation_gain: number;
  /** Average speed in meters/second */
  average_speed: number;
  /** Max speed in meters/second */
  max_speed: number;
  /** Start coordinates [lat, lng] */
  start_latlng: [number, number] | null;
  /** End coordinates [lat, lng] */
  end_latlng: [number, number] | null;
  /** Polyline-encoded summary of the route */
  map: {
    /** Full resolution polyline (detailed activities only) */
    polyline?: string;
    /** Reduced resolution polyline */
    summary_polyline: string;
  };
}

/**
 * Strava athlete information from the API.
 * Subset of fields returned with token exchange.
 */
export interface StravaAthlete {
  /** Unique athlete ID */
  id: number;
  /** First name */
  firstname: string;
  /** Last name */
  lastname: string;
  /** Profile picture URL (medium) */
  profile_medium: string;
  /** Profile picture URL (large) */
  profile: string;
  /** City */
  city: string | null;
  /** State/region */
  state: string | null;
  /** Country */
  country: string | null;
}

/**
 * Activity data returned from the strava-exchange Edge Function (camelCase).
 * This is different from StravaActivity which uses snake_case.
 */
export interface StravaExchangeActivity {
  id: number;
  name: string;
  type: string;
  sportType: string;
  startDate: string;
  distance: number;
  movingTime: number;
  totalElevationGain: number;
}

/**
 * Response from the strava-exchange Edge Function.
 */
export interface StravaExchangeResponse {
  success?: boolean;
  /** Athlete information */
  athlete: {
    id: number;
    firstName: string;
  };
  /** Most recent activity (if available) */
  activity: StravaExchangeActivity | null;
  /** Decoded coordinates from the activity polyline */
  coordinates: Coordinate[] | null;
}

/**
 * Error response from the strava-exchange Edge Function.
 */
export interface StravaExchangeError {
  error: string;
  details?: string;
}

/**
 * Activity summary as returned by the Edge Function API (camelCase).
 * This is the raw format before mapping to storage format.
 */
export interface StravaActivitySummaryAPI {
  id: number;
  name: string;
  type: string;
  sportType: string;
  startDate: string;
  distance: number;
  totalElevationGain: number;
  movingTime: number;
  summaryPolyline: string;
}

/**
 * Lightweight activity summary for the activity list.
 * Contains enough info for display + summary_polyline for mini route preview.
 */
export interface StravaActivitySummary {
  /** Unique activity ID */
  id: number;
  /** User-provided activity name */
  name: string;
  /** Activity type (Run, Ride, Swim, etc.) */
  type: string;
  /** Sport type (more specific than type) */
  sport_type: string;
  /** ISO 8601 start date (local time) */
  start_date_local: string;
  /** Distance in meters */
  distance: number;
  /** Total elevation gain in meters */
  total_elevation_gain: number;
  /** Moving time in seconds */
  moving_time: number;
  /** Reduced resolution polyline for mini preview */
  summary_polyline: string;
}

/**
 * Response from strava-exchange Edge Function in list mode.
 * Returns multiple activities with summary data for selection UI.
 * Note: API returns camelCase, which is mapped to snake_case for storage.
 */
export interface StravaActivitiesListResponse {
  success: true;
  mode: "list";
  athlete: {
    id: number;
    firstName: string;
  };
  activities: StravaActivitySummaryAPI[];
  /** Access token for fetching activity details later */
  accessToken: string;
}

/**
 * Activities data stored in localStorage after OAuth callback.
 * Used by the activity selection page before user picks an activity.
 */
export interface StravaActivitiesData {
  /** Athlete info for personalization */
  athlete: {
    id: number;
    firstName: string;
  };
  /** List of recent activities to choose from */
  activities: StravaActivitySummary[];
  /** Access token for fetching activity details */
  accessToken: string;
  /** Unix timestamp (ms) when data was fetched */
  fetchedAt: number;
}

/**
 * Request body for exchanging an authorization code for activities list.
 */
export interface StravaExchangeActivitiesRequest {
  /** The OAuth authorization code from Strava callback */
  code: string;
  /** Mode to request activity list instead of single activity */
  mode: "list";
  /** Whether to include activity data */
  fetchActivity: boolean;
  /** Optional Unix timestamp to filter activities after this date */
  after?: number;
  /** Optional Unix timestamp to filter activities before this date */
  before?: number;
}

/**
 * Request body for fetching activities using an existing access token.
 */
export interface StravaFetchActivitiesRequest {
  /** The Strava access token from initial OAuth exchange */
  accessToken: string;
  /** Mode to request activity list */
  mode: "list";
  /** Whether to include activity data */
  fetchActivity: boolean;
  /** Optional Unix timestamp to filter activities after this date */
  after?: number;
  /** Optional Unix timestamp to filter activities before this date */
  before?: number;
}

/**
 * Destination to redirect to after Strava flow completes.
 * Stored in localStorage before starting OAuth.
 */
export interface StravaDestination {
  /** Collection ID to navigate to (null for collection list page) */
  collectionId: string | null;
  /** Handle for the collection list page (e.g., "A", "B", "featured") */
  collectionListHandle: CollectionListHandle;
  /** Unix timestamp (ms) when destination was stored */
  createdAt: number;
}
